import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import "../../css/Toast.css";

const ToastContext = createContext(null);
let nextToastId = 0;

const toastTypes = new Set(["success", "error", "warning", "info"]);

const getToastType = (type) => (toastTypes.has(type) ? type : "info");

const toastIcons = {
  success: "✓",
  error: "!",
  warning: "!",
  info: "i",
};

const ToastItem = ({ toast, onClose }) => {
  useEffect(() => {
    const timeoutId = setTimeout(() => onClose(toast.id), toast.duration);
    return () => clearTimeout(timeoutId);
  }, [onClose, toast.duration, toast.id]);

  return (
    <div
      className={`app-toast app-toast-${toast.type}`}
      role="status"
      style={{ "--toast-duration": `${toast.duration}ms` }}
    >
      <span className="app-toast-icon" aria-hidden="true">
        {toastIcons[toast.type]}
      </span>
      <div className="app-toast-content">
        {toast.title && (
          <strong className="app-toast-title">{toast.title}</strong>
        )}
        {toast.message && (
          <span className="app-toast-message">{toast.message}</span>
        )}
      </div>
      <button
        type="button"
        className="app-toast-close"
        aria-label="Close notification"
        onClick={() => onClose(toast.id)}
      >
        <span aria-hidden="true">x</span>
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((toastId) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== toastId),
    );
  }, []);

  const addToast = useCallback(
    ({ title, message, type = "info", duration = 4000 }) => {
      const id = `${Date.now()}-${nextToastId++}`;
      setToasts((currentToasts) => [
        ...currentToasts,
        {
          id,
          title,
          message,
          type: getToastType(type),
          duration,
        },
      ]);
      return id;
    },
    [],
  );

  const toast = useCallback(
    (message, options = {}) => addToast({ ...options, message }),
    [addToast],
  );

  return (
    <ToastContext.Provider value={{ addToast, toast, removeToast }}>
      {children}
      <div className="app-toast-viewport" aria-live="polite" aria-atomic="true">
        {toasts.map((toastItem) => (
          <ToastItem
            key={toastItem.id}
            toast={toastItem}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used inside ToastProvider");
  }

  return context;
};

export default ToastProvider;
